import { useEffect, useId, useRef, useState, type ChangeEvent } from "react";
import { createPortal } from "react-dom";
import { sendFeedback } from "../apiClient.js";
import { collectClientContext } from "../lib/clientContext.js";
import { discordInviteUrl } from "../lib/communityLinks.js";
import { imageFileToDataUrl } from "../lib/screenshot.js";
import { useT } from "../lib/i18n/store.js";
import { AutoTextarea } from "./AutoTextarea.js";

const KINDS = [
  { value: "bug", labelKey: "feedback.kindBug", Icon: BugIcon },
  { value: "idea", labelKey: "feedback.kindIdea", Icon: IdeaIcon },
  { value: "other", labelKey: "feedback.kindOther", Icon: ChatIcon }
] as const;

/** Max screenshots one feedback item may carry (matches the server cap). */
const MAX_SCREENSHOTS = 10;

/**
 * The "Feedback" modal for testers: posts a bug / idea / note to /api/feedback
 * (with the current URL), so the closed beta has a first-class channel instead
 * of relying on out-of-band pings.
 *
 * It is a *controlled* component (`open` / `onClose`) so its trigger can live
 * inside the topbar's collapsing menu while the modal itself is rendered at the
 * topbar's top level. Keeping it out of the menu subtree means closing the menu
 * (or an outside-click that dismisses it) never unmounts the open modal.
 *
 * The modal is rendered through a portal to document.body: its trigger lives
 * inside the sticky `.topbar`, whose `backdrop-filter` makes it the containing
 * block for fixed-position descendants — so an inline modal would collapse into
 * the header instead of covering the viewport. Portaling escapes that.
 */
export function FeedbackModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const t = useT();
  // Optional public Discord invite (VITE_DISCORD_URL, read through
  // discordInviteUrl like every other surface): shown only when it is set to an
  // https:// link, so an unset or malformed value never renders a dead link.
  const discordUrl = discordInviteUrl() ?? undefined;
  const [kind, setKind] = useState<string>("bug");
  // One radio group per modal instance (the kind cards are native radios).
  const kindName = useId();
  const [message, setMessage] = useState("");
  const [contact, setContact] = useState("");
  // User-attached screenshots as JPEG data URLs (their own real screenshots —
  // reliable, unlike an html2canvas re-render), up to MAX_SCREENSHOTS.
  const [screenshots, setScreenshots] = useState<string[]>([]);
  const [status, setStatus] = useState<"idle" | "sending" | "sent" | "error">("idle");
  const [error, setError] = useState<string | null>(null);
  // Mirror of `screenshots` for the async file/paste handlers to read the current
  // count without being re-created (and so the paste listener stays subscribed).
  const screenshotsRef = useRef<string[]>([]);
  screenshotsRef.current = screenshots;
  // A text selection that begins inside the modal but is released over the
  // backdrop fires a click whose target is the backdrop — which would dismiss the
  // modal mid-selection. Only treat a backdrop click as a dismiss when the press
  // ALSO started on the backdrop itself.
  const pointerDownOnBackdrop = useRef(false);

  function close(): void {
    onClose();
    setKind("bug");
    setMessage("");
    setContact("");
    setScreenshots([]);
    setStatus("idle");
    setError(null);
  }

  async function applyImageFile(file: File): Promise<void> {
    if (screenshotsRef.current.length >= MAX_SCREENSHOTS) {
      setError(t("feedback.imageTooMany", { max: MAX_SCREENSHOTS }));
      return;
    }
    const dataUrl = await imageFileToDataUrl(file);
    if (!dataUrl) {
      setError(t("feedback.imageTooLarge"));
      return;
    }
    // Append, capped — the functional update is the source of truth for the cap.
    setScreenshots((prev) => (prev.length >= MAX_SCREENSHOTS ? prev : [...prev, dataUrl]));
  }

  async function handleFile(event: ChangeEvent<HTMLInputElement>): Promise<void> {
    const files = event.target.files ? Array.from(event.target.files) : [];
    // Reset the input so picking the same file again still fires onChange.
    event.target.value = "";
    setError(null);
    const remaining = MAX_SCREENSHOTS - screenshotsRef.current.length;
    if (remaining <= 0) {
      setError(t("feedback.imageTooMany", { max: MAX_SCREENSHOTS }));
      return;
    }
    if (files.length > remaining) setError(t("feedback.imageTooMany", { max: MAX_SCREENSHOTS }));
    for (const file of files.slice(0, remaining)) {
      await applyImageFile(file);
    }
  }

  // Paste a screenshot from the clipboard (Ctrl+V) anywhere in the modal. Kept in
  // a ref so the document-level listener always calls the latest handler without
  // re-subscribing on every keystroke. Only image items are consumed — a normal
  // text paste into the fields is left untouched.
  const applyImageFileRef = useRef(applyImageFile);
  applyImageFileRef.current = applyImageFile;
  useEffect(() => {
    if (!open) return;
    const onPaste = (event: ClipboardEvent): void => {
      const items = event.clipboardData?.items;
      if (!items) return;
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.kind === "file" && item.type.startsWith("image/")) {
          const file = item.getAsFile();
          if (file) {
            event.preventDefault();
            void applyImageFileRef.current(file);
            return;
          }
        }
      }
    };
    document.addEventListener("paste", onPaste);
    return () => document.removeEventListener("paste", onPaste);
  }, [open]);

  async function submit(): Promise<void> {
    if (!message.trim() || status === "sending") return;
    setStatus("sending");
    setError(null);
    try {
      await sendFeedback({
        kind,
        message: message.trim(),
        contact: contact.trim() || undefined,
        url: window.location.href,
        context: collectClientContext(),
        screenshots: screenshots.length > 0 ? screenshots : undefined
      });
      setStatus("sent");
    } catch (err) {
      setStatus("error");
      setError(err instanceof Error ? err.message : t("feedback.genericError"));
    }
  }

  if (!open) return null;

  return createPortal(
    <div
      className="admin-modal-backdrop"
      onMouseDown={(event) => {
        pointerDownOnBackdrop.current = event.target === event.currentTarget;
      }}
      onClick={(event) => {
        if (event.target === event.currentTarget && pointerDownOnBackdrop.current) close();
      }}
    >
      <div
        className="admin-modal feedback-modal"
        role="dialog"
        aria-modal="true"
        aria-label={t("feedback.dialogLabel")}
        onClick={(event) => event.stopPropagation()}
      >
        <button type="button" className="admin-modal-close" onClick={close} aria-label={t("feedback.close")}>
          ×
        </button>
        {status === "sent" ? (
          <div className="feedback-done">
            <span className="feedback-done-icon" aria-hidden="true">
              <CheckIcon />
            </span>
            <h2 className="feedback-title">{t("feedback.thanksTitle")}</h2>
            <p className="feedback-sub">{t("feedback.thanksSub")}</p>
            <button type="button" className="crash-btn crash-btn-primary" onClick={close}>
              {t("feedback.closeButton")}
            </button>
          </div>
        ) : (
          <>
            <h2 className="feedback-title">{t("feedback.title")}</h2>
            <p className="feedback-sub">
              {t("feedback.subtitle")}
            </p>
            {/* The kind picker is a real radio group drawn as icon cards: labels,
                not buttons, so the global gold button:hover fill never reaches
                them, and arrow keys move between kinds like any radio set. */}
            <fieldset className="feedback-kinds">
              <legend className="sr-only">{t("feedback.kindLegend")}</legend>
              {KINDS.map(({ value, labelKey, Icon }) => (
                <label key={value} className={kind === value ? "feedback-kind is-active" : "feedback-kind"}>
                  <input
                    type="radio"
                    name={kindName}
                    value={value}
                    checked={kind === value}
                    onChange={() => setKind(value)}
                  />
                  <Icon />
                  <span className="feedback-kind-label">{t(labelKey)}</span>
                </label>
              ))}
            </fieldset>
            <AutoTextarea
              className="feedback-text"
              rows={5}
              placeholder={t("feedback.messagePlaceholder")}
              value={message}
              onChange={(event) => setMessage(event.target.value)}
            />
            <input
              className="feedback-contact"
              type="text"
              placeholder={t("feedback.contactPlaceholder")}
              value={contact}
              onChange={(event) => setContact(event.target.value)}
            />
            <div className="feedback-attach">
              {screenshots.map((src, index) => (
                <div className="feedback-preview" key={index}>
                  <img src={src} alt={t("feedback.attachImage")} />
                  <button
                    type="button"
                    className="feedback-preview-remove"
                    onClick={() => setScreenshots((prev) => prev.filter((_, i) => i !== index))}
                    aria-label={t("feedback.removeImage")}
                  >
                    ×
                  </button>
                </div>
              ))}
              {screenshots.length < MAX_SCREENSHOTS && (
                <label className="feedback-attach-label">
                  <input type="file" accept="image/*" multiple onChange={handleFile} />
                  <ImageIcon />
                  <span>{t("feedback.attachImage")}</span>
                </label>
              )}
            </div>
            <span className="feedback-attach-hint">
              {screenshots.length > 0 ? `${screenshots.length}/${MAX_SCREENSHOTS}` : ""}
              {screenshots.length < MAX_SCREENSHOTS ? ` ${t("feedback.pasteHint")}` : ""}
            </span>
            {error && <p className="feedback-error">{error}</p>}
            <div className="feedback-actions">
              <button type="button" className="crash-btn crash-btn-ghost" onClick={close}>
                {t("feedback.cancel")}
              </button>
              <button
                type="button"
                className="crash-btn crash-btn-primary"
                disabled={!message.trim() || status === "sending"}
                onClick={submit}
              >
                {status === "sending" ? t("feedback.sending") : t("feedback.send")}
              </button>
            </div>
            {discordUrl && (
              <div className="feedback-community">
                <a className="feedback-discord" href={discordUrl} target="_blank" rel="noreferrer noopener">
                  <span className="feedback-discord-logo" aria-hidden="true">
                    <DiscordIcon />
                  </span>
                  <span className="feedback-discord-text">
                    <span className="feedback-discord-title">{t("feedback.discordCta")}</span>
                    <span className="feedback-discord-sub">{t("feedback.discordSub")}</span>
                  </span>
                  <ExternalIcon />
                </a>
              </div>
            )}
          </>
        )}
      </div>
    </div>,
    document.body
  );
}

/** Shared props for the modal's line icons (24-unit grid, currentColor stroke). */
function lineIconProps(size: number, strokeWidth = 1.75) {
  return {
    width: size,
    height: size,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth,
    strokeLinecap: "round",
    strokeLinejoin: "round",
    "aria-hidden": true,
    focusable: false
  } as const;
}

function BugIcon() {
  return (
    <svg {...lineIconProps(22)}>
      <path d="M9 7.5a3 3 0 0 1 6 0" />
      <rect x="7" y="8" width="10" height="12" rx="5" />
      <path d="M12 11.5V20" />
      <path d="M10 5.3 8.5 3M14 5.3l1.5-2.3" />
      <path d="M7.6 10.5 4.5 9M7 14H3.5M7.6 17.5l-3 1.5M16.4 10.5 19.5 9M17 14h3.5M16.4 17.5l3 1.5" />
    </svg>
  );
}

function IdeaIcon() {
  return (
    <svg {...lineIconProps(22)}>
      <path d="M8.5 14.5A6 6 0 1 1 15.5 14.5c-.9.8-1.5 1.6-1.5 2.5h-4c0-.9-.6-1.7-1.5-2.5z" />
      <path d="M10 19.5h4M10.5 22h3" />
    </svg>
  );
}

function ChatIcon() {
  return (
    <svg {...lineIconProps(22)}>
      <path d="M6.5 4h11A2.5 2.5 0 0 1 20 6.5v8a2.5 2.5 0 0 1-2.5 2.5H11l-4.5 3.5V17A2.5 2.5 0 0 1 4 14.5v-8A2.5 2.5 0 0 1 6.5 4z" />
      <path d="M8.5 10.5h.01M12 10.5h.01M15.5 10.5h.01" strokeWidth={2.6} />
    </svg>
  );
}

function ImageIcon() {
  return (
    <svg {...lineIconProps(16)}>
      <rect x="3" y="4" width="18" height="16" rx="2.5" />
      <circle cx="8.5" cy="9.5" r="1.5" />
      <path d="m21 15-4.5-4.5L7 20" />
    </svg>
  );
}

function ExternalIcon() {
  return (
    <svg className="feedback-discord-arrow" {...lineIconProps(16)}>
      <path d="M7 17 17 7" />
      <path d="M8.5 7H17v8.5" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg {...lineIconProps(24, 2)}>
      <path d="M5 12.5 9.5 17 19 7.5" />
    </svg>
  );
}

/** Discord's logo mark (Simple Icons, CC0). Drawn in the text colour: Discord's
 *  brand rules allow the mark in white, black or blurple, never recoloured gold. */
function DiscordIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" focusable="false">
      <path d="M20.317 4.3698a19.7913 19.7913 0 00-4.8851-1.5152.0741.0741 0 00-.0785.0371c-.211.3753-.4447.8648-.6083 1.2495-1.8447-.2762-3.68-.2762-5.4868 0-.1636-.3933-.4058-.8742-.6177-1.2495a.077.077 0 00-.0785-.037 19.7363 19.7363 0 00-4.8852 1.515.0699.0699 0 00-.0321.0277C.5334 9.0458-.319 13.5799.0992 18.0578a.0824.0824 0 00.0312.0561c2.0528 1.5076 4.0413 2.4228 5.9929 3.0294a.0777.0777 0 00.0842-.0276c.4616-.6304.8731-1.2952 1.226-1.9942a.076.076 0 00-.0416-.1057c-.6528-.2476-1.2743-.5495-1.8722-.8923a.077.077 0 01-.0076-.1277c.1258-.0943.2517-.1923.3718-.2914a.0743.0743 0 01.0776-.0105c3.9278 1.7933 8.18 1.7933 12.0614 0a.0739.0739 0 01.0785.0095c.1202.099.246.1981.3728.2924a.077.077 0 01-.0066.1276 12.2986 12.2986 0 01-1.873.8914.0766.0766 0 00-.0407.1067c.3604.698.7719 1.3628 1.225 1.9932a.076.076 0 00.0842.0286c1.961-.6067 3.9495-1.5219 6.0023-3.0294a.077.077 0 00.0313-.0552c.5004-5.177-.8382-9.6739-3.5485-13.6604a.061.061 0 00-.0312-.0286zM8.02 15.3312c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9555-2.4189 2.157-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.9555 2.4189-2.1569 2.4189zm7.9748 0c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9554-2.4189 2.1569-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.946 2.4189-2.1568 2.4189Z" />
    </svg>
  );
}
